import { Deserializable } from '../protocols/deserializable';
import { AccountType } from './account-type';
import { GoverningJurisdictionType } from '../employers/employer-history/types/governing-jurisdiction-type';
import { StatusPill } from '../shared/status-pill';
import { AccountStatusType } from './account-status-type';
import { AccountStatusEnum } from './enum/account-status.enum';
import { ContributionAccountDetails } from './contribution-account-details';
import { VariableBenefitAccountDetails } from './variable-benefit-account-details';
import { AnnuityPensionAccountDetails } from './annuity-pension-account-details';
import { AccountTypeIdEnum } from './enum/account-type-id.enum';

export class MemberAccount implements Deserializable {
  public id!: number;
  public typeId!: AccountTypeIdEnum;
  public type!: AccountType;
  public memberId!: number;
  public member!: string;
  public employerId!: number | null;
  public employer!: string;
  public spouseId!: number | null;
  public spouseName!: string | null;
  public statusId!: AccountStatusEnum;
  public status!: AccountStatusType;
  // TODO: SPP-3441 mocked until the backend's account reads return this.
  public statusChangedDate?: string | null;
  public createdDate!: string;
  public contributionAccountDetails!: ContributionAccountDetails;
  public variableBenefitAccountDetails!: VariableBenefitAccountDetails | null;
  public annuityAccountDetails!: AnnuityPensionAccountDetails | null;
  public governingJurisdiction!: GoverningJurisdictionType;
  public totalBalance!: number;
  // TODO: SPP-3390 replace with a Deserializable class once the backend's account reads return this.
  public spousalRolloverAccount?: {
    spousalAccountId: number;
    spousalAccountIdPrefix: string;
    successorMemberId: number;
    successorName: string;
    spousalAccountStatusId: AccountStatusEnum;
  } | null;
  // TODO: SPP-3390 replace with a Deserializable class once the backend's account reads return this.
  public spousalRolloverSource?: {
    sourceAccountId: number;
    sourceAccountIdPrefix: string;
    sourceMemberId: number;
    sourceMemberName: string;
    sourceAccountStatusId: AccountStatusEnum;
  } | null;

  // Not From API
  public displayableId!: string;

  public onDeserialize(): void {
    const Deserialize = window?.injector?.Deserialize;
    if (this.type) {
      this.type = Deserialize?.instanceOf(AccountType, this.type);
    }

    if (this.governingJurisdiction) {
      this.governingJurisdiction = Deserialize?.instanceOf(GoverningJurisdictionType, this.governingJurisdiction);
    }

    this.displayableId = this.id?.toString().padStart(8, '0') ?? '';

    if (this.contributionAccountDetails) {
      this.contributionAccountDetails = Deserialize?.instanceOf(
        ContributionAccountDetails,
        this.contributionAccountDetails
      );
    }

    if (this.variableBenefitAccountDetails) {
      this.variableBenefitAccountDetails = Deserialize?.instanceOf(
        VariableBenefitAccountDetails,
        this.variableBenefitAccountDetails
      );
    }

    if (this.annuityAccountDetails) {
      this.annuityAccountDetails = Deserialize?.instanceOf(AnnuityPensionAccountDetails, this.annuityAccountDetails);
    }
  }

  public static getStatusPillForStatus(statusId: AccountStatusEnum): StatusPill {
    switch (statusId) {
      case AccountStatusEnum.Pending:
        return new StatusPill(0, $localize`Pending`, '#FB932A', '#FFEAD1', '#B24819');
      case AccountStatusEnum.Active:
        return new StatusPill(0, $localize`Active`, '#78AB00', '#E8F7D9', '#395200');
      case AccountStatusEnum.Inactive:
        return new StatusPill(1, $localize`Inactive`, '#B9B9B9', '#FAFAFA', '#3B3B3C');
      case AccountStatusEnum.Struck:
        return new StatusPill(1, $localize`Struck`, '#FF8E93', '#FFEAEB', '#9D0209');
      case AccountStatusEnum.Suspended:
        return new StatusPill(1, $localize`Suspended`, '#FF8E93', '#FFEAEB', '#9D0209');
    }
  }

  public static getSpousalPill(): StatusPill {
    return new StatusPill(0, $localize`Spousal`, '#68C3EB', '#DCEBFA', '#104255');
  }

  public getStatusPill(): StatusPill {
    return MemberAccount.getStatusPillForStatus(this.statusId);
  }

  public isMemberSelfContributionAccount(): boolean {
    return this.isContributionAccount() && !this.employerId && !this.spouseId;
  }

  public isMemberEmployerContributionAccount(): boolean {
    return this.isContributionAccount() && !!this.employerId && !this.spouseId;
  }

  public isSpousalContributionAccount(): boolean {
    return this.isContributionAccount() && !!this.spouseId && !this.employerId;
  }

  public isInactiveOrStruck(): boolean {
    return this.statusId === AccountStatusEnum.Inactive || this.statusId === AccountStatusEnum.Struck;
  }

  public isStruck(): boolean {
    return this.statusId === AccountStatusEnum.Struck;
  }

  public isContributionAccount(): boolean {
    return this.typeId === AccountTypeIdEnum.ContributionAccount;
  }

  public getSplitStatusPill(): StatusPill | null {
    return this.annuityAccountDetails?.getSplitStatusPill() ?? null;
  }
}
